import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Syringe, CheckCircle2, AlertTriangle, Calendar, Loader2 } from 'lucide-react';
import { api } from './api';

/** Immunisation — list of scheduled vaccines with mark-as-completed + alerts. */
const Immunization = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData((await api.vaccines()).data); } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (key, completed) => {
    setSavingKey(key);
    try {
      await api.markVaccine({ key, completed });
      await refresh();
    } finally { setSavingKey(''); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 via-white to-white pb-24" data-testid="child-immunization">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/childrens-health')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-sky-500 flex items-center justify-center">
              <Syringe className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Immunisation</h1>
              <p className="text-xs text-gray-500 mt-0.5">Schedule, reminders, and one-tap mark-as-completed.</p>
            </div>
          </div>
        </header>

        {loading && <div className="text-sm text-gray-500">Loading schedule…</div>}

        {data && (
          <>
            {data.next && (
              <div className="rounded-2xl bg-blue-50 border-2 border-blue-200 p-4 mb-4 flex items-start gap-3" data-testid="next-vaccine">
                <Calendar className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-widest">Next due</p>
                  <p className="text-base font-bold text-gray-900">{data.next.name}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{data.next.info} · in ~{Math.max(0, data.next.delta_months)} mo</p>
                </div>
                <button
                  onClick={() => toggle(data.next.key, true)}
                  disabled={savingKey === data.next.key}
                  className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-blue-600 hover:brightness-110 text-white shadow-sm"
                  data-testid={`mark-${data.next.key}`}
                >
                  Mark done
                </button>
              </div>
            )}

            {data.overdue && data.overdue.length > 0 && (
              <div className="rounded-2xl bg-rose-50 border-2 border-rose-200 p-4 mb-4" data-testid="overdue-vaccines">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-rose-700" />
                  <p className="text-sm font-bold text-rose-900">{data.overdue.length} overdue</p>
                </div>
                <ul className="space-y-1.5">
                  {data.overdue.map((v) => (
                    <li key={v.key} className="text-sm text-gray-800 flex items-center gap-2">
                      <span className="flex-1">{v.name} <span className="text-xs text-gray-500">({Math.abs(v.delta_months)} mo overdue)</span></span>
                      <button
                        onClick={() => toggle(v.key, true)}
                        disabled={savingKey === v.key}
                        className="text-[11px] font-semibold rounded-md px-2 py-1 bg-rose-600 hover:brightness-110 text-white"
                        data-testid={`mark-${v.key}`}
                      >
                        Mark done
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 px-1">Full schedule</p>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm divide-y divide-gray-50">
              {data.schedule.map((v) => {
                const done = data.completed_keys.includes(v.key);
                return (
                  <div key={v.key} className="px-4 py-3 flex items-center gap-3" data-testid={`schedule-${v.key}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {done ? <CheckCircle2 className="h-4 w-4" /> : <Syringe className="h-3.5 w-3.5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{v.name}</p>
                      <p className="text-xs text-gray-500">at {v.age_months === 0 ? 'birth' : `${v.age_months} mo`} · {v.info}</p>
                    </div>
                    <button
                      onClick={() => toggle(v.key, !done)}
                      disabled={savingKey === v.key}
                      className={`text-[11px] font-semibold rounded-md px-2.5 py-1 ${
                        done ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-blue-600 text-white hover:brightness-110'
                      }`}
                      data-testid={`toggle-${v.key}`}
                    >
                      {savingKey === v.key ? <Loader2 className="h-3 w-3 animate-spin" /> : done ? 'Undo' : 'Mark done'}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <p className="text-[11px] text-gray-400 text-center mt-6">
          Schedule is a guide — confirm dates with your pediatrician.
        </p>
      </div>
    </div>
  );
};

export default Immunization;
